// react and js
import * as Yup from "yup";
import { React, useState, useRef, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { useTranslation } from "react-i18next";

// mui
import { Collapse, Card, Box, Typography, Grid } from "@mui/material";

// components
import backendRequest from "../../components/BackendRequest";

// hooks

// layouts

// pages

// theme
import {
  AuthenticationButton,
  AuthenticationTextField,
  AuthenticationAlertError,
  AuthenticationAlertSuccess,
  B3Tiny,
} from "../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../utils/common";
import {
  executeRecaptchaV3,
  preloadRecaptchaV3,
} from "../../utils/recaptchaV3";

// i18n
import { localizeExternalUrl } from "../../i18n/languages";

// widgets

// app

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget() {
  const { t, i18n } = useTranslation();
  const wrapperRef = useRef(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");
  const [resetCooldown, setResetCooldown] = useState(0);

  useEffect(() => {
    preloadRecaptchaV3();
  }, []);

  useEffect(() => {
    if (resetCooldown <= 0) return undefined;
    const timerId = setTimeout(() => {
      setResetCooldown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => clearTimeout(timerId);
  }, [resetCooldown]);

  useEffect(() => {
    if (!error) return undefined;
    const timerId = setTimeout(() => setError(false), 8000);
    return () => clearTimeout(timerId);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timerId = setTimeout(() => setSuccess(false), 8000);
    return () => clearTimeout(timerId);
  }, [success]);

  const PasswordResetSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("passwordReset.errors.emailInvalid"))
      .required(t("passwordReset.errors.emailRequired")),
  });

  const handlePasswordReset = async (values, setSubmitting) => {
    if (navigator.onLine === true) {
      setError(false);
      setSuccess(false);
      setResetCooldown(60);

      const recaptchaValue = await executeRecaptchaV3("password_reset");

      const sendData = {
        request_type: "dashboard",
        route_info: "authentication",
        client: {
          mode: "password_reset",
          email: values.email,
          return_url: localizeExternalUrl(
            `${process.env.REACT_APP_URL_FRONTEND_DASHBOARD}/new-password`,
            i18n.language
          ),
          // return_url: "http://localhost:3000/new-password",
          recaptcha_value: recaptchaValue,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("answer");

          if (checkKey === true) {
            if (contentFromBackend.answer.message === "success") {
              setReturnMessage(<TranslatedMessage tKey="passwordReset.status.success" />);
              setSuccess(true);
              setError(false);
            } else {
              setReturnMessage(<TranslatedMessage tKey="passwordReset.status.checkEmail" />);
              setError(true);
            }
          } else {
            combineReturnMessage(contentFromBackend, setReturnMessage);
            setError(true);
          }
          setSubmitting(false);
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setReturnMessage(<TranslatedMessage tKey="passwordReset.status.unknownError" />);
            setError(true);
            setSubmitting(false);
          }
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: PasswordResetSchema,
    onSubmit: (values, { setSubmitting }) => {
      handlePasswordReset(values, setSubmitting);
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        direction="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 800,
          px: 2,
        }}
      >
        <Card
          sx={{
            background: "#F2F2F2",
            borderColor: "#6D6E70",
            boxShadow: "0px 0px 5px 0px rgb(255, 255, 255)",
          }}
        >
          <Box sx={{ p: 4 }} dir="ltr">
            <Typography
              variant="h2"
              sx={{
                mt: 0,
                mb: 0,
              }}
            >
              {t("passwordReset.title")}
            </Typography>

            <Box sx={{ mt: 1, mb: 1 }} dir="ltr">
              <Typography
                variant="contentMiddle"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {t("passwordReset.subtitle")}
              </Typography>
            </Box>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.email && errors.email) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.email && errors.email) === true
                    ? errors.email
                    : t("passwordReset.labels.email")}
                </Typography>

                <AuthenticationTextField
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type="email"
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                />

                <Collapse
                  ref={wrapperRef}
                  in={error}
                  timeout="auto"
                  unmountOnExit
                >
                  <AuthenticationAlertError
                    severity="error"
                    sx={{ mt: 0, mb: 1 }}
                  >
                    {returnMessage}
                  </AuthenticationAlertError>
                </Collapse>

                <Collapse
                  ref={wrapperRef}
                  in={success}
                  timeout="auto"
                  unmountOnExit
                >
                  <AuthenticationAlertSuccess
                    severity="success"
                    sx={{ mt: 0, mb: 1 }}
                  >
                    {returnMessage}
                  </AuthenticationAlertSuccess>
                </Collapse>

                <B3Tiny
                  fullWidth
                  variant="outlined"
                  component={RouterLink}
                  to="/login"
                  sx={{
                    mt: 1,
                    mb: 1,
                    fontSize: (theme) => theme.typography.b1Small.fontSize,
                    fontWeight: (theme) => theme.typography.b1Small.fontWeight,
                    lineHeight: (theme) => theme.typography.b1Small.lineHeight,
                    letterSpacing: (theme) =>
                      theme.typography.b1Small.letterSpacing,
                  }}
                >
                  {t("passwordReset.links.backToLogin")}
                </B3Tiny>

                <AuthenticationButton
                  fullWidth
                  variant="contained"
                  disabled={resetCooldown > 0}
                  loading={isSubmitting}
                  type="submit"
                  sx={{ mt: 0 }}
                >
                  {resetCooldown > 0
                    ? t("passwordReset.submitButtonCooldown", { seconds: resetCooldown })
                    : t("passwordReset.submitButton")}
                </AuthenticationButton>
              </Form>
            </FormikProvider>
          </Box>
        </Card>
      </Box>
    </Grid>
  );
}
